import React, { useMemo, useContext } from 'react'
import { useParams } from 'react-router-dom'
import {
  GetPreviewDetailsQuery,
  useGetPreviewPageQuery,
  GetPreviewPageQuery,
} from '@sketch/gql-types'
import { RouteParams } from '@sketch/modules-common'

export interface PreviewContextProps {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  currentPage?: GetPreviewPageQuery['preview']['page']
  isLoadingPage: boolean
  hasErrorLoading: boolean
}

export const PreviewContext = React.createContext<
  PreviewContextProps | undefined
>(undefined)

type PreviewContextProviderProps = {
  children: React.ReactNode
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
}

export function PreviewContextProvider({
  children,
  preview,
}: PreviewContextProviderProps) {
  const { pageUUID } = useParams<RouteParams<'PREVIEW_PAGE_VIEW'>>()
  const currentPageUUID = pageUUID || preview.pages.entries[0].uuid || undefined

  const { data, loading, error } = useGetPreviewPageQuery({
    variables: {
      previewIdentifier: preview.identifier,
      pageUUID: currentPageUUID!,
    },
    skip: !currentPageUUID,
  })

  const currentPage = currentPageUUID && data ? data.preview.page : undefined

  const contextValue = useMemo(() => {
    return {
      preview,
      currentPage,
      isLoadingPage: loading,
      hasErrorLoading: !!error,
    }
  }, [preview, currentPage, error, loading])

  return (
    <PreviewContext.Provider value={contextValue}>
      {children}
    </PreviewContext.Provider>
  )
}

export function usePreview() {
  const contextValue = useContext(PreviewContext)

  return contextValue
}
